import React from 'react';
import { ChevronDown, ChevronUp, Zap, Code, Battery, Layers } from 'lucide-react';

interface BESSOptimizationStepProps {
  expandedSection: string | null;
  toggleSection: (section: string) => void;
}

export const BESSOptimizationStep: React.FC<BESSOptimizationStepProps> = ({
  expandedSection,
  toggleSection,
}) => {
  return (
    <div className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden">
      <div className="px-6 py-4 border-b border-gray-100 bg-gray-50/50">
        <div className="flex items-center justify-between cursor-pointer" onClick={() => toggleSection('bess-strategy')}>
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-xl bg-gray-100 text-gray-700 flex items-center justify-center text-sm font-semibold">3</div>
            <h3 className="text-base font-semibold text-black">BESS Optimization Strategy</h3>
          </div>
          {expandedSection === 'bess-strategy' ? <ChevronUp className="w-4 h-4 text-gray-600" /> : <ChevronDown className="w-4 h-4 text-gray-600" />}
        </div>
      </div>
      {expandedSection === 'bess-strategy' && (
        <div className="p-6">
          <div className="space-y-4">
            <p className="text-sm text-gray-600">
              Our BESS optimization uses a modular architecture that separates planning strategy from simulation execution,
              allowing for flexible optimization approaches while maintaining consistent performance evaluation.
            </p>

            {/* Modular Architecture Overview */}
            <div className="bg-gray-50 p-4 rounded-lg">
              <p className="text-xs font-semibold text-gray-700 mb-3">
                <Layers className="w-3 h-3 inline mr-1" />
                MODULAR ARCHITECTURE
              </p>
              <div className="space-y-3">
                <div className="bg-white p-3 rounded border border-gray-200">
                  <p className="text-xs font-bold text-brand-primary mb-2">1. PLANNER MODULE</p>
                  <p className="text-xs text-gray-600 mb-2">
                    Creates optimal charge/discharge schedules based on forecasted data
                  </p>
                  <ul className="text-xs space-y-1">
                    <li className="flex items-start">
                      <span className="text-brand-primary mr-1">•</span>
                      <span>Perfect Foresight: Uses complete knowledge of future prices</span>
                    </li>
                    <li className="flex items-start">
                      <span className="text-gray-400 mr-1">•</span>
                      <span>Rule-Based: Simple time-of-use logic (coming soon)</span>
                    </li>
                    <li className="flex items-start">
                      <span className="text-gray-400 mr-1">•</span>
                      <span>ML-Based: Pattern learning from historical data (future)</span>
                    </li>
                  </ul>
                </div>

                <div className="bg-white p-3 rounded border border-gray-200">
                  <p className="text-xs font-bold text-gray-700 mb-2">2. SIMULATOR MODULE</p>
                  <p className="text-xs text-gray-600 mb-2">
                    Executes any schedule against actual data with real-world constraints
                  </p>
                  <ul className="text-xs space-y-1">
                    <li className="flex items-start">
                      <span className="text-gray-500 mr-1">•</span>
                      <span>Validates power and energy constraints</span>
                    </li>
                    <li className="flex items-start">
                      <span className="text-gray-500 mr-1">•</span>
                      <span>Applies round-trip efficiency losses</span>
                    </li>
                    <li className="flex items-start">
                      <span className="text-gray-500 mr-1">•</span>
                      <span>Calculates actual costs and savings</span>
                    </li>
                  </ul>
                </div>
              </div>
            </div>

            {/* Current Implementation */}
            <div className="bg-gray-50 p-4 rounded-lg">
              <p className="text-xs font-semibold text-gray-700 mb-2">
                <Zap className="w-3 h-3 inline mr-1" />
                PERFECT FORESIGHT PLANNER
              </p>
              <div className="grid grid-cols-2 gap-3">
                <div className="bg-white p-3 rounded border border-gray-200">
                  <p className="text-xs font-bold text-brand-primary mb-1">OPTIMIZATION STRATEGY</p>
                  <ul className="text-xs space-y-1">
                    <li className="flex items-start">
                      <span className="text-brand-primary mr-1">•</span>
                      <span>24-hour look-ahead scheduling</span>
                    </li>
                    <li className="flex items-start">
                      <span className="text-brand-primary mr-1">•</span>
                      <span>Value scoring for each hour</span>
                    </li>
                    <li className="flex items-start">
                      <span className="text-brand-primary mr-1">•</span>
                      <span>Pre-charges for HP hours (18:00-22:00)</span>
                    </li>
                    <li className="flex items-start">
                      <span className="text-brand-primary mr-1">•</span>
                      <span>Generator replacement prioritization</span>
                    </li>
                  </ul>
                </div>
                <div className="bg-white p-3 rounded border border-gray-200">
                  <p className="text-xs font-bold text-gray-700 mb-1">KEY FEATURES</p>
                  <ul className="text-xs space-y-1">
                    <li className="flex items-start">
                      <span className="text-gray-500 mr-1">•</span>
                      <span>Accounts for client consumption</span>
                    </li>
                    <li className="flex items-start">
                      <span className="text-gray-500 mr-1">•</span>
                      <span>Infers generator power from data</span>
                    </li>
                    <li className="flex items-start">
                      <span className="text-gray-500 mr-1">•</span>
                      <span>Three savings components calculated</span>
                    </li>
                    <li className="flex items-start">
                      <span className="text-gray-500 mr-1">•</span>
                      <span>Hour-by-hour simulation output</span>
                    </li>
                  </ul>
                </div>
              </div>
            </div>

            {/* Simplified Flow Diagram */}
            <div className="bg-gray-50 p-4 rounded-lg">
              <p className="text-xs font-semibold text-gray-700 mb-2">
                <Code className="w-3 h-3 inline mr-1" />
                IMPLEMENTATION FLOW
              </p>
              <pre className="text-xs bg-white p-3 rounded border border-gray-300 overflow-x-auto">
{`# 1. Configure BESS System
bess_config = BESSConfig(
    power_kw=500,
    energy_kwh=1000,
    round_trip_efficiency=0.90,
    depth_of_discharge=0.90
)

# 2. Create Planner
planner = PerfectForesightPlanner(bess_config)

# 3. Generate Schedule
schedule = planner.plan_schedule(
    forecast_data=aligned_data,
    generator_power_kw=inferred_generator,
    planning_horizon_hours=24
)

# 4. Run Simulator
simulator = BESSSimulator(bess_config)
results = simulator.simulate(
    schedule=schedule,
    actual_data=historical_data
)

# 5. Calculate Savings
annual_savings = simulator.calculate_annual_savings(results)
# Energy Arbitrage: $18,011
# Peak Shaving: $14,405
# Generator Replacement: $19,656
# Total: $52,071`}</pre>
            </div>

            <div className="bg-gray-50 p-3 rounded-lg">
              <p className="text-xs font-semibold text-gray-700 mb-1">
                <Battery className="w-3 h-3 inline mr-1" />
                Key System Parameters
              </p>
              <div className="grid grid-cols-3 gap-2 text-xs">
                <div>
                  <p className="text-gray-600">Round-trip Efficiency</p>
                  <p className="font-bold">90%</p>
                </div>
                <div>
                  <p className="text-gray-600">Depth of Discharge</p>
                  <p className="font-bold">90%</p>
                </div>
                <div>
                  <p className="text-gray-600">Planning Horizon</p>
                  <p className="font-bold">24 hours</p>
                </div>
              </div>
            </div>

            {/* Benefits of Modular Approach */}
            <div className="bg-blue-50 border border-blue-200 p-3 rounded-lg">
              <p className="text-xs font-semibold text-blue-900 mb-1">Benefits of Modular Architecture</p>
              <ul className="text-xs text-blue-800 space-y-1">
                <li>• Easy to add new planning strategies without changing simulation</li>
                <li>• Consistent evaluation metrics across all planners</li>
                <li>• Separation of concerns: strategy vs execution</li>
                <li>• Testable and maintainable codebase</li>
              </ul>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};